/**
 * Drives real camera sensor zoom (the MediaStreamTrack `zoom` constraint) from
 * a two-finger pinch. The gesture surface is supplied by the caller because the
 * <video> is painted under the camera overlays and never receives touches.
 */

import { RefObject, useEffect, useRef, useState } from "react";

interface ZoomRange {
  min: number;
  max: number;
}

interface UsePinchZoomOptions {
  /** Optional zoom ceiling; defaults to the camera's full reported range. */
  maxZoom?: number;
}

interface UsePinchZoomReturn {
  zoomSupported: boolean;
  zoomLevel: number;
  zoomRange: ZoomRange;
}

export const usePinchZoom = (
  videoRef: RefObject<HTMLVideoElement>,
  gestureTargetRef: RefObject<HTMLElement>,
  streamReady: boolean,
  options?: UsePinchZoomOptions
): UsePinchZoomReturn => {
  const maxZoomCap = options?.maxZoom;

  const [zoomSupported, setZoomSupported] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomRange, setZoomRange] = useState<ZoomRange>({ min: 1, max: 1 });

  // Refs mirror state so the touch handlers (bound once) read current values
  // without re-binding on every zoom change.
  const zoomLevelRef = useRef(1);
  const zoomRangeRef = useRef<ZoomRange>({ min: 1, max: 1 });

  const pinchStartDistRef = useRef<number | null>(null);
  const pinchStartZoomRef = useRef(1);

  // applyConstraints is async and laggy if spammed during a pinch, so calls are
  // coalesced: while one is in flight we keep only the latest desired zoom.
  const applyInFlightRef = useRef(false);
  const pendingZoomRef = useRef<number | null>(null);

  const getVideoTrack = (): MediaStreamTrack | null => {
    const srcObject = videoRef.current?.srcObject;
    if (!srcObject || !(srcObject instanceof MediaStream)) {
      return null;
    }
    return srcObject.getVideoTracks()[0] ?? null;
  };

  useEffect(() => {
    const checkZoomSupport = () => {
      try {
        const videoTrack = getVideoTrack();
        if (!videoTrack) {
          setZoomSupported(false);
          return;
        }

        const capabilities = videoTrack.getCapabilities?.() as Record<string, unknown> | undefined;
        if (capabilities && "zoom" in capabilities && capabilities.zoom) {
          const zoomCap = capabilities.zoom as { min?: number; max?: number };
          const min = zoomCap.min ?? 1;
          const reportedMax = zoomCap.max ?? min;
          const max = maxZoomCap ? Math.min(reportedMax, maxZoomCap) : reportedMax;
          const range = { min, max };
          setZoomRange(range);
          zoomRangeRef.current = range;

          const settings = videoTrack.getSettings?.() as Record<string, unknown> | undefined;
          const raw = settings && "zoom" in settings ? (settings.zoom as number) : min;
          // Clamp to the (possibly maxZoom-capped) range so the baseline and
          // indicator never start outside the enforced range.
          const current = Math.max(min, Math.min(max, raw));
          setZoomLevel(current);
          zoomLevelRef.current = current;

          setZoomSupported(max > min);
        } else {
          setZoomSupported(false);
        }
      } catch (error) {
        console.warn("usePinchZoom: failed to check zoom support:", error);
        setZoomSupported(false);
      }
    };

    // Wait until the stream is ready before probing. `streamReady` is a state
    // value, so this effect re-runs when the stream actually attaches (a ref
    // dep would not, since ref mutations don't re-render). The short timeout is
    // just a settle margin — some tracks populate getCapabilities() a beat
    // after the stream is ready.
    if (!streamReady) {
      setZoomSupported(false);
      return;
    }
    const timeout = setTimeout(checkZoomSupport, 150);
    return () => clearTimeout(timeout);
  }, [streamReady, maxZoomCap]);

  const requestZoom = (level: number) => {
    const { min, max } = zoomRangeRef.current;
    const clamped = Math.max(min, Math.min(max, level));
    zoomLevelRef.current = clamped;
    setZoomLevel(clamped);
    pendingZoomRef.current = clamped;

    if (applyInFlightRef.current) {
      return;
    }
    void flushZoom();
  };

  const flushZoom = async () => {
    const videoTrack = getVideoTrack();
    if (!videoTrack) {
      pendingZoomRef.current = null;
      return;
    }
    applyInFlightRef.current = true;
    try {
      while (pendingZoomRef.current !== null) {
        const target = pendingZoomRef.current;
        pendingZoomRef.current = null;
        // @ts-ignore - `zoom` is not yet in TypeScript's MediaTrackConstraintSet
        await videoTrack.applyConstraints({ advanced: [{ zoom: target }] });
      }
    } catch (error) {
      console.error("usePinchZoom: failed to apply zoom:", error);
      pendingZoomRef.current = null;
    } finally {
      applyInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (!zoomSupported) {
      return;
    }
    const target = gestureTargetRef.current;
    if (!target) {
      return;
    }

    const touchDistance = (touches: TouchList): number => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        pinchStartDistRef.current = touchDistance(e.touches);
        pinchStartZoomRef.current = zoomLevelRef.current;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinchStartDistRef.current) {
        e.preventDefault(); // stop native page pinch-zoom
        const ratio = touchDistance(e.touches) / pinchStartDistRef.current;
        requestZoom(pinchStartZoomRef.current * ratio);
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        pinchStartDistRef.current = null;
      }
    };

    // iOS Safari also fires non-standard gesture events for pinch — block them
    const preventGesture = (e: Event) => e.preventDefault();

    target.addEventListener("touchstart", onTouchStart, { passive: false });
    target.addEventListener("touchmove", onTouchMove, { passive: false });
    target.addEventListener("touchend", onTouchEnd);
    target.addEventListener("touchcancel", onTouchEnd);
    target.addEventListener("gesturestart", preventGesture as EventListener);
    target.addEventListener("gesturechange", preventGesture as EventListener);

    return () => {
      target.removeEventListener("touchstart", onTouchStart);
      target.removeEventListener("touchmove", onTouchMove);
      target.removeEventListener("touchend", onTouchEnd);
      target.removeEventListener("touchcancel", onTouchEnd);
      target.removeEventListener("gesturestart", preventGesture as EventListener);
      target.removeEventListener("gesturechange", preventGesture as EventListener);
    };
    // Keyed on zoomSupported (real state) rather than gestureTargetRef.current:
    // ref mutations don't re-run effects. zoomSupported only flips true after
    // the stream is ready and the frame (incl. the gesture layer) has rendered,
    // so gestureTargetRef.current is reliably set when this runs.
  }, [zoomSupported]);

  return { zoomSupported, zoomLevel, zoomRange };
};
